import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import type { getCouponUpdateState } from "features/subscriptions/utils";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { del, patch, post } from "common/helpers/HTTP";
import type {
  AgencyPlan,
  AgencySubscription,
  CustomerInfo,
  DetailedWorkspace,
  Promotion,
  Workspace,
} from "common/types";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

async function createSubscriptionRequest(
  count: number,
  paymentSourceId: string,
  planPricingId: string,
  couponId: string,
): Promise<AgencyPlan> {
  return (
    await post(`subscriptions`, {
      seat_count: count,
      payment_source_id: paymentSourceId,
      item_price_id: planPricingId,
      coupon: couponId,
    })
  ).promotion;
}
async function updateSubscriptionRequest(
  count: number,
  paymentSourceId: string,
  planPricingId: string,
  couponUpdateState: CouponUpdateState,
): Promise<AgencyPlan> {
  return (
    await patch(`subscriptions`, {
      seat_count: count,
      payment_source_id: paymentSourceId,
      item_price_id: planPricingId,
      ...(couponUpdateState && {
        coupon: couponUpdateState.coupon,
        remove_coupon: couponUpdateState.removeCoupon,
      }),
    })
  ).promotion;
}

async function removeScheduledChangesRequest(): Promise<{
  subscription: AgencySubscription;
}> {
  return del(`subscriptions/changes`);
}

export default function useCustomerSubscriptionMutations() {
  const queryClient = useQueryClient();

  const {
    mutateAsync: createCustomerSubscription,
    isPending: isCreatingCustomerSubscription,
    error: createCustomerSubscriptionError,
  } = useMutation({
    mutationFn: ({
      count,
      paymentSourceId,
      planPricingId,
      couponId,
    }: {
      count: number;
      paymentSourceId: string;
      planPricingId: string;
      couponId: string;
    }) =>
      createSubscriptionRequest(
        count,
        paymentSourceId,
        planPricingId,
        couponId,
      ),
    onSuccess: (promotion) => {
      queryClient.setQueryData<Promotion[]>(
        CUSTOMER_QUERY_KEYS.promotions,
        (prevPromotions) => {
          if (prevPromotions) {
            return [...prevPromotions, promotion];
          } else {
            return [promotion];
          }
        },
      );
      queryClient.setQueryData<CustomerInfo>(
        CUSTOMER_QUERY_KEYS.customerInfo,
        (prevCustomerInfo) => {
          return {
            ...prevCustomerInfo,
            agency_subscription_status: promotion.subscription.status,
          };
        },
      );

      queryClient.setQueryData<Workspace[]>(
        CUSTOMER_QUERY_KEYS.workspaces.base,
        (prevWorkspaces) =>
          produce(prevWorkspaces, (draftWorkspaces) => {
            for (const workspace of draftWorkspaces) {
              if (workspace.role === "owner") {
                if (workspace.subscription_status === "in_trial") {
                  workspace.subscription_status = "cancelled";
                }
                workspace.agency_subscription_status = "active";
              }
            }
          }),
      );

      queryClient.setQueriesData<DetailedWorkspace>(
        { queryKey: CUSTOMER_QUERY_KEYS.workspaces.details() },
        (prevDetails) => {
          if (prevDetails?.role === "owner") {
            return produce(prevDetails, (draftWorkspaceDetails) => {
              if (draftWorkspaceDetails.subscription_status === "in_trial") {
                draftWorkspaceDetails.subscription_status = "cancelled";
              }
              draftWorkspaceDetails.agency_subscription_status = "active";
            });
          }
          return prevDetails;
        },
      );
    },
  });

  const {
    mutateAsync: updateCustomerSubscription,
    isPending: isUpdatingCustomerSubscription,
    error: updateCustomerSubscriptionError,
  } = useMutation({
    mutationFn: ({
      count,
      paymentSourceId,
      planPricingId,
      couponUpdateState,
    }: {
      count: number;
      paymentSourceId: string;
      planPricingId: string;
      couponUpdateState: CouponUpdateState;
    }) =>
      updateSubscriptionRequest(
        count,
        paymentSourceId,
        planPricingId,
        couponUpdateState,
      ),
    onSuccess: (updatedPromotion) => {
      queryClient.setQueryData<Promotion[]>(
        CUSTOMER_QUERY_KEYS.promotions,
        (prevPromotions) =>
          produce(prevPromotions, (promotionsDraft) => {
            for (let i = 0; i < promotionsDraft.length; i += 1) {
              const promotion = promotionsDraft[i];
              if (
                promotion.type === "agency" &&
                promotion.subscription.id === updatedPromotion.subscription.id
              ) {
                promotionsDraft[i] = updatedPromotion;
                return promotionsDraft;
              }
            }
          }),
      );
      queryClient.setQueryData<CustomerInfo>(
        CUSTOMER_QUERY_KEYS.customerInfo,
        (prevCustomerInfo) => {
          return {
            ...prevCustomerInfo,
            agency_subscription_status: updatedPromotion.subscription.status,
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: CUSTOMER_QUERY_KEYS.subscriptionKeys.renewalEstimate(),
      });
    },
  });

  const {
    mutateAsync: removeAgencyScheduledChanges,
    isPending: isRemovingAgencyScheduledChanges,
  } = useMutation({
    mutationFn: () => removeScheduledChangesRequest(),
    onSuccess: ({ subscription }) => {
      queryClient.setQueryData<Promotion[]>(
        CUSTOMER_QUERY_KEYS.promotions,
        (prevPromotions) =>
          produce(prevPromotions, (promotionsDraft) => {
            const promotion = promotionsDraft?.find(
              (draftPromotion): draftPromotion is AgencyPlan =>
                draftPromotion.type === "agency",
            );
            if (promotion) {
              promotion.subscription = subscription;
            }
          }),
      );
      queryClient.invalidateQueries({
        queryKey: CUSTOMER_QUERY_KEYS.subscriptionKeys.renewalEstimate(),
      });
    },
    onError: () => toast.error("Failed to cancel scheduled change"),
  });

  return {
    createCustomerSubscription,
    isCreatingCustomerSubscription,
    createCustomerSubscriptionError,

    updateCustomerSubscription,
    isUpdatingCustomerSubscription,
    updateCustomerSubscriptionError,

    removeAgencyScheduledChanges,
    isRemovingAgencyScheduledChanges,
  };
}
